import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaTag, FaPlus, FaTrash, FaTimes, FaCalendarAlt, FaPercent } from "react-icons/fa";

const D2Rebates = () => {
  const [rebates, setRebates] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.role === "DealershipAdmin" || user?.role === "Owner";

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    discountType: "FLAT",
    discountAmount: 0,
    applicableVehicles: "", // String to split by comma
    salespersonId: "",
    startDate: "",
    endDate: ""
  });

  const fetchSalespersons = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const filtered = (data.data || []).filter(u => u.role === "Salesperson");
        setSalespersons(filtered);
      }
    } catch (err) {
      console.error("Failed to fetch salespersons", err);
    }
  };

  const fetchRebates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-rebates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setRebates(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch rebates", err);
      toast.error("Failed to load rebates list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRebates();
    fetchSalespersons();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveRebate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      const vehicles = formData.applicableVehicles
        ? formData.applicableVehicles.split(",").map(v => v.trim()).filter(Boolean)
        : [];

      const payload = {
        ...formData,
        applicableVehicles: vehicles,
        salespersonId: formData.salespersonId || null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null
      };

      const res = await fetch(`${getBackendBaseUrl()}/d2-rebates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Coupon/Rebate added successfully!");
        setShowModal(false);
        setFormData({
          name: "",
          code: "",
          description: "",
          discountType: "FLAT",
          discountAmount: 0,
          applicableVehicles: "",
          salespersonId: "",
          startDate: "",
          endDate: ""
        });
        fetchRebates();
      } else {
        toast.error(data.message || "Failed to add rebate program");
      }
    } catch (err) {
      console.error("Error saving rebate", err);
      toast.error("Failed to add rebate program");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rebate?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-rebates/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Rebate deleted successfully");
        fetchRebates();
      }
    } catch (err) {
      toast.error("Failed to delete rebate");
    }
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl shadow-sm border border-indigo-100">
              <FaTag size={24} />
            </span>
            Rebates & Coupons
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Configure dealership discounts, manufacturer rebates, and guest coupon codes for calculations.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm"
          >
            <FaPlus size={12} /> Create Coupon/Rebate
          </button>
        )}
      </div>

      {/* Rebates Grid */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Rebate Program</th>
                <th className="px-6 py-4">Coupon Code</th>
                <th className="px-6 py-4">Discount</th>
                <th className="px-6 py-4">Attributed Salesperson</th>
                <th className="px-6 py-4">Applicability</th>
                <th className="px-6 py-4">Date Range</th>
                {isAdmin && <th className="px-6 py-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="px-6 py-10 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                      Loading rebates database...
                    </div>
                  </td>
                </tr>
              ) : rebates.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="px-6 py-12 text-center text-slate-400 text-sm italic">
                    No active rebates or coupon campaigns found.
                  </td>
                </tr>
              ) : (
                rebates.map((rebate) => (
                  <tr key={rebate._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-800 text-sm">{rebate.name}</div>
                      <div className="text-slate-400 text-xs mt-0.5">{rebate.description || "No description"}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-indigo-50 text-indigo-700 text-[11px] font-mono font-bold px-2.5 py-1.5 rounded-lg border border-indigo-100">
                        {rebate.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-extrabold text-slate-800 text-sm">
                        {rebate.discountType === "PERCENTAGE" ? `${rebate.discountAmount}%` : `$${rebate.discountAmount.toLocaleString()}`}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {rebate.salespersonId ? (
                        <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-lg border border-blue-100">
                          {rebate.salespersonId.firstName} {rebate.salespersonId.lastName}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs italic">Dealership Wide</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs font-medium text-slate-500">
                      {rebate.applicableVehicles && rebate.applicableVehicles.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {rebate.applicableVehicles.map((v, idx) => (
                            <span key={idx} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                              {v}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">All Vehicles</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400 flex items-center gap-1.5 mt-2.5">
                      <FaCalendarAlt size={11} className="text-slate-300" />
                      {rebate.startDate ? new Date(rebate.startDate).toLocaleDateString() : "Always"}
                      <span>-</span>
                      {rebate.endDate ? new Date(rebate.endDate).toLocaleDateString() : "Always"}
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(rebate._id)}
                          className="text-red-500 hover:text-red-700 font-semibold text-xs tracking-wide bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-200/30 flex items-center gap-1.5 ml-auto"
                        >
                          <FaTrash size={10} /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Admin Add Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight mb-2">Create Coupon / Rebate Program</h2>
            <p className="text-slate-400 text-xs mb-6 border-b pb-4">
              Create customer codes or dealer programs to deduct amounts during deal calculations.
            </p>
            
            <form onSubmit={handleSaveRebate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Program Name *</label>
                <input required type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" placeholder="e.g. Summer Rebate Special" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Coupon Code *</label>
                <input required type="text" name="code" value={formData.code} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all uppercase" placeholder="e.g. SUMMER1000" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Discount Amount ($ / %) *</label>
                <input required type="number" name="discountAmount" value={formData.discountAmount} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Discount Type</label>
                <select name="discountType" value={formData.discountType} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all">
                  <option value="FLAT">Flat Rate Discount ($)</option>
                  <option value="PERCENTAGE">Percentage Discount (%)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Assign to Salesperson (Optional)</label>
                <select name="salespersonId" value={formData.salespersonId} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all">
                  <option value="">-- Dealership Wide (No Specific Salesperson) --</option>
                  {salespersons.map(sp => (
                    <option key={sp._id} value={sp._id}>
                      {sp.firstName} {sp.lastName} ({sp.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Applicable Models (Comma separated)</label>
                <input type="text" name="applicableVehicles" value={formData.applicableVehicles} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" placeholder="e.g. Camry, RAV4 (Leave empty for all)" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Start Date</label>
                <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">End Date</label>
                <input type="date" name="endDate" value={formData.endDate} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Description</label>
                <textarea rows={2} name="description" value={formData.description} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" placeholder="Program conditions, eligibility..." />
              </div>

              <div className="col-span-1 sm:col-span-2 flex justify-end gap-3 mt-6 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-800 font-semibold text-sm transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm cursor-pointer"
                >
                  Create Program
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2Rebates;
